// AI Engine Dashboard - Refactored thin shell
// Tab content split into separate components to avoid Babel stack overflow

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain, Activity, Users, RefreshCw, Settings, BarChart3,
  Zap, MessageSquare, DollarSign
} from 'lucide-react';

// Lazy loaded tab components (already separate files)
import NegotiationTab from '../components/NegotiationTab';
import SalesIntelligenceTab from '../components/SalesIntelligenceTab';

const AIEngineDashboard = () => {
  const { isSuperAdmin } = useAuth();
  // 21 Oct 2026 security hardening: ai_enabled/auto_approval_enabled and every tolerance/
  // threshold here are business-critical (they decide AUTO_APPROVED vs ESCALATED on real
  // purchases) — the backend now rejects this whole PUT /ai-engine/config for anyone but a
  // Super Admin, so the UI disables every control here the same way, instead of letting a
  // non-super-admin see a save attempt silently fail.
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('trade-brain');

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/ai-engine/dashboard');
      setDashboard(response.data);
      setConfig(response.data.config);
    } catch (error) {
      toast.error('Failed to load AI dashboard');
    }
  }, []);

  useEffect(() => {
    fetchDashboard().then(() => setLoading(false));
  }, [fetchDashboard]);

  const updateConfig = async (newConfig) => {
    try {
      await api.put('/ai-engine/config', { ...config, ...newConfig });
      setConfig({ ...config, ...newConfig });
      toast.success('Configuration updated');
    } catch (error) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><RefreshCw className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="ai-engine-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Brain className="h-7 w-7 text-primary" />AI Trading Intelligence</h1>
          <p className="text-muted-foreground">Market intelligence and automated trading decisions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setConfigModalOpen(true)} data-testid="ai-config-btn"><Settings className="h-4 w-4 mr-2" />Configure{!isSuperAdmin && ' (view only)'}</Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><BarChart3 className="h-4 w-4" />Market Intelligence</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{dashboard?.market_intelligence?.skus_analyzed || 0}</div><p className="text-xs text-muted-foreground">Last computed — paused (Offers & Purchases retired)</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Users className="h-4 w-4" />Suppliers Scored</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{dashboard?.supplier_intelligence?.total_suppliers || 0}</div><p className="text-xs text-muted-foreground">Last computed — paused</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Zap className="h-4 w-4" />AI Decisions</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{dashboard?.ai_decisions_today || 0}</div></CardContent></Card>
      </div>

      {/* AI Status */}
      {config && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div><CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" />AI Engine Status</CardTitle></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">AI Enabled{!isSuperAdmin && <span className="text-xs font-normal"> (Super Admin only)</span>}</span><Switch checked={config?.ai_enabled} disabled={!isSuperAdmin} onCheckedChange={(c) => updateConfig({ ai_enabled: c })} data-testid="ai-enabled-switch" /></div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-muted rounded-lg"><p className="text-xs text-muted-foreground">Auto-Action Threshold</p><p className="text-lg font-semibold">≥ {((config?.auto_action_threshold || 0.95) * 100).toFixed(0)}%</p></div>
              <div className="p-3 bg-muted rounded-lg"><p className="text-xs text-muted-foreground">Human Review Range</p><p className="text-lg font-semibold">{((config?.human_review_threshold || 0.7) * 100).toFixed(0)}% - {(((config?.auto_action_threshold || 0.95) - 0.01) * 100).toFixed(0)}%</p></div>
              <div className="p-3 bg-muted rounded-lg"><p className="text-xs text-muted-foreground">No-Action Threshold</p><p className="text-lg font-semibold">&lt; {((config?.human_review_threshold || 0.7) * 100).toFixed(0)}%</p></div>
              <div className="p-3 bg-muted rounded-lg"><p className="text-xs text-muted-foreground">AI Model</p><p className="text-lg font-semibold">GPT-4o</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - each content is a separate component */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="trade-brain" data-testid="trade-brain-tab"><Brain className="h-4 w-4 mr-2" />AI Trade Brain</TabsTrigger>
          <TabsTrigger value="market" data-testid="market-tab"><BarChart3 className="h-4 w-4 mr-2" />Market Intelligence</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="suppliers-tab"><Users className="h-4 w-4 mr-2" />Supplier Scores</TabsTrigger>
          <TabsTrigger value="negotiation" data-testid="negotiation-tab-trigger"><MessageSquare className="h-4 w-4 mr-2" />AI Negotiation</TabsTrigger>
          <TabsTrigger value="sales-intel" data-testid="sales-intel-tab-trigger"><DollarSign className="h-4 w-4 mr-2" />Sales Intelligence</TabsTrigger>
        </TabsList>
        <TabsContent value="trade-brain"><TradeBrainTab /></TabsContent>
        <TabsContent value="market"><MarketIntelTab /></TabsContent>
        <TabsContent value="suppliers"><SupplierScoresTab /></TabsContent>
        <TabsContent value="negotiation"><NegotiationTab /></TabsContent>
        <TabsContent value="sales-intel"><SalesIntelligenceTab /></TabsContent>
      </Tabs>

      {/* Config Modal */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContent><DialogHeader><DialogTitle>AI Engine Configuration</DialogTitle><DialogDescription>Configure AI trading intelligence thresholds{!isSuperAdmin && ' — Super Admin only, view only for you'}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Auto-Action Threshold (%)</Label><Input type="number" min="0" max="100" disabled={!isSuperAdmin} value={(config?.auto_action_threshold || 0.95) * 100} onChange={(e) => updateConfig({ auto_action_threshold: parseFloat(e.target.value) / 100 })} /></div>
              <div className="space-y-2"><Label>Human Review Threshold (%)</Label><Input type="number" min="0" max="100" disabled={!isSuperAdmin} value={(config?.human_review_threshold || 0.70) * 100} onChange={(e) => updateConfig({ human_review_threshold: parseFloat(e.target.value) / 100 })} /></div>
            </div>
            <Button variant="outline" onClick={() => setConfigModalOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Inline lightweight tab components (each under 100 lines)
function TradeBrainTab() {
  const [decisions, setDecisions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  useEffect(() => { api.get('/ai-engine/trade-brain?limit=50').then(r => setDecisions((r.data.data || []).filter(d => d.sku_id !== 'summary'))).catch(() => {}); }, []);
  const analyze = async () => { setAnalyzing(true); try { await api.post('/ai-engine/trade-brain/analyze-all?limit=20'); toast.success('Analysis started'); setTimeout(() => { api.get('/ai-engine/trade-brain?limit=50').then(r => setDecisions((r.data.data || []).filter(d => d.sku_id !== 'summary'))); setAnalyzing(false); }, 10000); } catch(e) { toast.error('Failed'); setAnalyzing(false); } };
  const badge = (d) => { const s = { BUY_AGGRESSIVELY: 'bg-green-600', BUY_SELECTIVELY: 'bg-green-400', HOLD: 'bg-gray-500', STOP_BUYING: 'bg-red-500', CLEAR_STOCK: 'bg-purple-500' }; return <span className={`px-2 py-0.5 rounded text-xs text-white ${s[d] || 'bg-gray-500'}`}>{d?.replace(/_/g, ' ')}</span>; };
  return (
    <Card><CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">AI Trading Decisions</CardTitle><Button onClick={analyze} disabled={analyzing} data-testid="analyze-all-btn"><Zap className="h-4 w-4 mr-2" />{analyzing ? 'Analyzing...' : 'Analyze SKUs'}</Button></div></CardHeader>
    <CardContent>
      {decisions.length === 0 ? <div className="text-center py-8 text-muted-foreground">No decisions yet. Click "Analyze SKUs".</div> : (
        <div className="space-y-1">{decisions.slice(0, 20).map(d => (
          <div key={d.id} className="flex items-center justify-between p-2 border-b hover:bg-muted/30 cursor-pointer text-sm" onClick={() => setSelected(d)} data-testid={`decision-row-${d.sku_id}`}>
            <span className="font-mono w-32 truncate">{d.sku_id}</span>
            <span>{badge(d.decision)}</span>
            <span className="text-xs w-16 text-right">{(d.confidence_score * 100).toFixed(0)}%</span>
            <span className="text-xs w-24 text-right">{d.recommended_max_price ? `$${d.recommended_max_price.toFixed(2)}` : '-'}</span>
          </div>
        ))}</div>
      )}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}><DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto"><DialogHeader><DialogTitle>AI Trade Decision</DialogTitle><DialogDescription>{selected?.sku_name || selected?.sku_id}</DialogDescription></DialogHeader>
        {selected && <div className="space-y-3">
          <div className="flex items-center gap-2">{badge(selected.decision)}<span className="text-sm">Confidence: {(selected.confidence_score*100).toFixed(0)}%</span></div>
          <div className="p-3 bg-muted rounded"><p className="text-xs font-semibold mb-1">Reasoning</p><p className="text-sm">{selected.reasoning}</p></div>
          {selected.risk_assessment && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded"><p className="text-xs font-semibold mb-1">Risk</p><p className="text-sm">{selected.risk_assessment}</p></div>}
          {selected.key_factors?.length > 0 && <div className="p-3 border rounded"><p className="text-xs font-semibold mb-1">Key Factors</p><ul className="text-sm list-disc pl-4">{selected.key_factors.map((f,i) => <li key={i}>{f}</li>)}</ul></div>}
          {selected.target_reasoning && <div className="p-3 bg-muted rounded"><p className="text-xs font-semibold mb-1">Target Recommendation</p><p className="text-sm">{selected.target_reasoning}</p></div>}
        </div>}
      </DialogContent></Dialog>
    </CardContent></Card>
  );
}

function MarketIntelTab() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => { api.get('/ai-engine/market-intelligence?limit=100').then(r => setData(r.data.data || [])).catch(() => {}); }, []);
  const filtered = data.filter(i => !search || i.sku_id?.toLowerCase().includes(search.toLowerCase()));
  return (
    <Card><CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">SKU Market Intelligence</CardTitle><Input placeholder="Search SKU..." value={search} onChange={e => setSearch(e.target.value)} className="w-64" data-testid="market-search" /></div></CardHeader>
    <CardContent><div className="space-y-1 text-xs">
      <div className="grid grid-cols-7 font-semibold p-2 bg-muted/50 border-b">
        <span>SKU</span><span>Target</span><span>Median</span><span>Trend</span><span>Volatility</span><span>Demand</span><span>Supply</span>
      </div>
      {filtered.slice(0, 20).map(i => (
        <div key={i.sku_id} className="grid grid-cols-7 p-2 border-b hover:bg-muted/30" data-testid={`market-row-${i.sku_id}`}>
          <span className="font-mono truncate">{i.sku_id}</span>
          <span>{i.target_price ? `$${i.target_price.toFixed(2)}` : '-'}</span>
          <span>{i.offer_price_median ? `$${i.offer_price_median.toFixed(2)}` : '-'}</span>
          <span>{i.price_trend_7d}</span>
          <span>{i.price_volatility_index != null ? `${i.price_volatility_index.toFixed(1)}%` : '-'}</span>
          <span>{i.demand_signal}</span>
          <span>{i.supply_signal}</span>
        </div>
      ))}
    </div></CardContent></Card>
  );
}

function SupplierScoresTab() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get('/ai-engine/supplier-intelligence?limit=100').then(r => setData(r.data.data || [])).catch(() => {}); }, []);
  return (
    <Card><CardHeader><CardTitle className="text-base">Supplier Intelligence Scores</CardTitle></CardHeader>
    <CardContent><div className="space-y-1 text-xs">
      <div className="grid grid-cols-6 font-semibold p-2 bg-muted/50 border-b"><span>Supplier</span><span>Score</span><span>Price vs Market</span><span>Conversion</span><span>Deals</span><span>Status</span></div>
      {data.map(s => (
        <div key={s.supplier_name} className="grid grid-cols-6 p-2 border-b hover:bg-muted/30" data-testid={`supplier-row-${s.supplier_name}`}>
          <span className="font-medium truncate">{s.supplier_name}</span>
          <span className={s.final_supplier_score >= 7 ? 'text-green-600 font-bold' : s.final_supplier_score >= 5 ? 'text-yellow-600' : 'text-red-600'}>{s.final_supplier_score?.toFixed(1)}</span>
          <span className={s.avg_price_vs_market < 0 ? 'text-green-600' : 'text-red-600'}>{s.avg_price_vs_market > 0 ? '+' : ''}{s.avg_price_vs_market?.toFixed(1)}%</span>
          <span>{s.deal_conversion_rate?.toFixed(1)}%</span>
          <span>{s.total_deals}</span>
          <span>{s.is_active ? 'Active' : 'Inactive'}</span>
        </div>
      ))}
    </div></CardContent></Card>
  );
}

export default AIEngineDashboard;
